/*
 * Smooth scrolling (Lenis, self-hosted in assets/lenis.min.js).
 *
 * Lenis eases the scroll delta but performs a REAL window scroll — unlike transform-based
 * libraries — so `scrollY` stays accurate, native scroll events still fire, and every
 * `position: sticky` in the theme (header, filter sidebar, PDP info column) keeps working
 * untouched. That is the reason this is a drop-in rather than a refactor.
 *
 * Scope:
 *   - Pointer devices only. `syncTouch` is off, so touch scrolling stays native (iOS
 *     momentum is already good and Lenis fights it); gating on `(pointer: fine)` also
 *     keeps the rAF loop off phones entirely.
 *   - Skipped outright under prefers-reduced-motion. Forced smooth scrolling is a real
 *     problem for vestibular disorders, not a style preference.
 *
 * Integration points, all handled here so no section markup changes:
 *   - Scroll lock: every panel, sheet and the cart drawer already toggles
 *     `html.sd-scroll-lock`. One MutationObserver on that class drives stop()/start(),
 *     so anything added later is covered for free.
 *   - Nested scrollers: `allowNestedScroll` lets Lenis hand wheel events back to any
 *     scrollable ancestor, and `prevent` names ours explicitly as a backstop — including
 *     markup injected after load (cart drawer re-render, quick-add), because `prevent`
 *     is evaluated per event rather than baked into the DOM as attributes.
 *   - bfcache: iOS restores the page with the JS heap intact, so a stopped instance
 *     would come back stopped. `pageshow` resyncs it.
 */
(function () {
  if (typeof Lenis === 'undefined') return;

  // Touch scrolling stays native, so there is nothing to gain on a touch device.
  if (!window.matchMedia('(pointer: fine)').matches) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* Elements with their own overflow. Lenis walks the composed path of each wheel
     event and hands control back when one of these matches.
  */
  const NESTED_SCROLLERS = [
    '.product__info-container', // PDP info column
    '.sd-plp-aside', // collection / search filter sidebar
    '.sd-dropdown__panel', // custom select
    '.sd-nav__panel', // mega menu + search sheets
    '.sd-sheet', // mobile sort / filter sheets
    '.cart-drawer', // Dawn's drawer
    '.sd-qa-popup', // quick-add / cart edit popup
  ].join(',');

  const lenis = new Lenis({
    /* The feel dial. Lower = heavier and more floaty, higher = snappier and closer to
       native. 0.1 (the library default) reads as input lag on a trackpad — the page
       keeps travelling after your fingers stop. 0.15 keeps the easing but stays
       responsive. Above ~0.25 there is little point running Lenis at all. */
    lerp: 0.15,
    smoothWheel: true,
    syncTouch: false,
    allowNestedScroll: true,
    anchors: true, // in-page anchor links ease instead of jumping
    autoRaf: true, // Lenis drives its own rAF loop
    respectReducedMotion: true,
    prevent: (node) => node?.matches?.(NESTED_SCROLLERS),
  });

  window.SdScroll = lenis;

  /* The theme's single source of truth for "the page must not scroll". Mirroring it
     here means the drawer, both nav panels, the facet sheets and the quick-add popup
     all lock Lenis without any of them knowing Lenis exists. */
  const syncLock = () => {
    if (document.documentElement.classList.contains('sd-scroll-lock')) lenis.stop();
    else lenis.start();
  };
  new MutationObserver(syncLock).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  });
  syncLock();

  // Restored from the back/forward cache: the instance survives, its state may be stale.
  window.addEventListener('pageshow', () => {
    syncLock();
    lenis.resize();
  });
})();
